const INVALID = '—';

function safeN(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Integer or decimal — consistent thousands separator, no trailing zeros beyond decimals.
 * Uses tabular-nums rendering assumption (CSS font-variant-numeric: tabular-nums at call site).
 *
 * fmtNumber(1234567)      → "1,234,567"
 * fmtNumber(12345.678, 2) → "12,345.68"
 */
export function fmtNumber(value: unknown, decimals?: number): string {
  const n = safeN(value);
  if (n === null) return INVALID;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 0
  }).format(n);
}

/**
 * Percentage — always 0-2 decimal places.
 *
 * fmtPercent(99.5)  → "99.5%"
 * fmtPercent(100)   → "100%"
 * fmtPercent(0.123) → "0.12%"
 */
export function fmtPercent(value: unknown, decimals = 1): string {
  const n = safeN(value);
  if (n === null) return INVALID;
  return `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(n)}%`;
}

/**
 * Money — symbol prefix, 2 decimal places.
 *
 * fmtMoney(1234.5, 'USD') → "$1,234.50"
 * fmtMoney(99, 'EUR')     → "€99.00"
 */
export function fmtMoney(value: unknown, currency = 'USD'): string {
  const n = safeN(value);
  if (n === null) return INVALID;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}
